import { Link } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import Logo from '@/components/shared/Logo'

export default function About() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>

            <nav aria-label="Breadcrumb" className="hidden sm:block">
              <ol className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <li>
                  <Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">
                    Home
                  </Link>
                </li>
                <li aria-hidden>•</li>
                <li className="text-gray-700 dark:text-gray-300 font-medium">About</li>
              </ol>
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-10 text-center">
            About AsiliFlow
          </h1>

          {/* Mission */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Our Mission
            </h2>

            <div className="text-gray-700 dark:text-gray-300 leading-relaxed space-y-4">
              <p>
                AsiliFlow exists to transform how healthcare agencies manage their operations.
                Our purpose is simple and structural: streamline scheduling, centralize documentation,
                track client authorizations, and give agencies tighter control over day-to-day operations.
              </p>

              <p>
                In a market defined by staffing shortages, regulatory requirements, and operational complexity,
                AsiliFlow provides the one thing every agency needs but rarely has: system-level clarity.
              </p>

              <p>We do this by:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Replacing manual scheduling with smart, conflict-free shift management</li>
                <li>Eliminating missed authorizations through automated alert tracking</li>
                <li>Centralizing shift notes, incidents, and care-plan workflows in one system</li>
                <li>Reducing administrative burden so staff can operate at full capacity</li>
                <li>Restoring operational control in environments defined by unpredictability</li>
                <li>Ensuring every shift is documented, tracked, and audit-ready</li>
              </ul>

              <p>
                Our mission is not merely operational—it is foundational.
                We build the digital infrastructure that allows healthcare agencies to
                serve patients with confidence, free from the bottlenecks, risks,
                and administrative drag that limit care quality.
              </p>

              <p className="font-medium text-primary-600">
                AsiliFlow — Healthcare operations, simplified.
              </p>
            </div>
          </section>

          {/* Why AsiliFlow */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Why AsiliFlow?</h2>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 sm:p-8">
              <ul className="space-y-5">
                {[
                  {
                    title: 'Reduce Administrative Burden',
                    text: 'Simplify scheduling, documentation review, and daily coordination.',
                  },
                  {
                    title: 'Ensure Compliance',
                    text: 'Stay audit-ready with automated authorization alerts and full audit trails.',
                  },
                  {
                    title: 'Improve Efficiency',
                    text: 'Replace fragmented spreadsheets and status chasing with one shared operations system.',
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-gray-900 dark:text-gray-100">{item.title}:</strong>{' '}
                      <span className="text-gray-700 dark:text-gray-300">{item.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Our Values */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Security First',
                  text: 'Your data is protected with enterprise-grade security and HIPAA compliance.',
                },
                {
                  title: 'User-Centric',
                  text: 'Designed by healthcare professionals for healthcare professionals.',
                },
                {
                  title: 'Continuous Innovation',
                  text: 'We evolve continuously to meet the changing needs of healthcare agencies.',
                },
              ].map((value, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 transition hover:shadow-md"
                >
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 mb-2">{value.title}</h3>
                  <p className="text-gray-700 dark:text-gray-300">{value.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* The Company */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">The Company</h2>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 sm:p-8 space-y-4">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                AsiliFlow is a product of{' '}
                <strong className="text-gray-900 dark:text-gray-100">Asili LLC</strong>,
                a Virginia-registered company building digital infrastructure for the healthcare industry.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Alongside AsiliFlow, we also build{' '}
                <a
                  href="https://asiliguide.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  AsiliGuide
                </a>
                , a healthcare onboarding and compliance platform that helps agencies verify caregivers,
                automate regulatory tracking, and eliminate manual paperwork.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                The name <em>Asili</em> comes from Swahili, meaning <em>&ldquo;origin&rdquo;</em> or{' '}
                <em>&ldquo;foundation.&rdquo;</em> It reflects our commitment to building the operational
                foundation that healthcare agencies need to deliver excellent care.
              </p>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Ready to Get Started?</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Join healthcare agencies nationwide already streamlining operations with AsiliFlow.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
            >
              Start Your 30-Day Free Trial
            </Link>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-sm text-gray-500 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-300">
              Terms of Service
            </Link>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-300">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
